import { useState } from 'react';
import PropTypes from 'prop-types';

import { trans } from '@spotahome/soyuz/client';

import Initial from './states/Initial';
import Uploading from './states/Uploading';
import Uploaded from './states/Uploaded';
import Done from './states/Done';
import Error from './states/Error';

const STATUS = {
  INITIAL: 'initialStatus',
  UPLOADING: 'uploading',
  DONE: 'done',
  UPLOADED: 'uploaded',
  ERROR: 'error'
};

const delayTwoSeconds = action => {
  setTimeout(() => {
    action();
  }, 2000);
};

const FileUploader = ({
  preloaded = false,
  uploadFile = async () => {},
  id = '',
  onFileUploaded = () => {},
  onDelete = () => {},
  title = '',
  required = false,
  className = '',
  dataTest = '',
  maxFileSizeBytes = null
}) => {
  const [selectedFileName, setSelectedFileName] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [uploadStatus, setUploadStatus] = useState(
    preloaded ? STATUS.UPLOADED : STATUS.INITIAL
  );

  const handleChangeFile = async ({ target }) => {
    if (target.files.length <= 0) {
      return;
    }

    const selectedFile = target.files[0];

    setUploadStatus(STATUS.UPLOADING);

    try {
      setErrorMessage(null);
      if (maxFileSizeBytes && selectedFile.size > maxFileSizeBytes) {
        const sizeInMB = maxFileSizeBytes / 1024 / 1024;
        setErrorMessage(
          trans('document-upload.file.size.error', {
            size: sizeInMB
          })
        );
        throw new Error(`File size exceeds ${sizeInMB} MB`);
      }

      const { filePath } = (await uploadFile(selectedFile, id)) || {};
      onFileUploaded({ id, filePath });
      setUploadStatus(STATUS.DONE);

      delayTwoSeconds(() => {
        setUploadStatus(STATUS.UPLOADED);
        setSelectedFileName(selectedFile && selectedFile.name);
      });
    } catch (error) {
      setUploadStatus(STATUS.ERROR);
      setSelectedFileName(null);

      delayTwoSeconds(() => {
        setUploadStatus(STATUS.INITIAL);
      });
    }
  };

  const handleDeleteFile = () => {
    setUploadStatus(STATUS.INITIAL);
    setSelectedFileName(null);

    onDelete(id);
  };

  const renderTitle = () =>
    required ? `${title} ${trans('document-upload.required')}` : title;

  switch (uploadStatus) {
    case STATUS.UPLOADING:
      return <Uploading className={className} />;
    case STATUS.UPLOADED:
      return (
        <Uploaded
          className={className}
          title={renderTitle()}
          fileName={selectedFileName}
          onDelete={handleDeleteFile}
        />
      );
    case STATUS.DONE:
      return <Done className={className} />;
    case STATUS.ERROR:
      return (
        <Error className={className} title={errorMessage || renderTitle()} />
      );
    default:
      return (
        <Initial
          className={className}
          title={renderTitle()}
          onChangeFile={handleChangeFile}
          dataTest={dataTest}
        />
      );
  }
};

FileUploader.propTypes = {
  className: PropTypes.string,
  id: PropTypes.string,
  onDelete: PropTypes.func,
  onFileUploaded: PropTypes.func,
  preloaded: PropTypes.bool,
  required: PropTypes.bool,
  title: PropTypes.string,
  uploadFile: PropTypes.func,
  dataTest: PropTypes.string,
  maxFileSizeBytes: PropTypes.number
};

export default FileUploader;
